import React, { useEffect, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Toolbar,
  List,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardIcon from "@mui/icons-material/Dashboard";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { app_title } from "../../config";
import {
  clearPartnerCookieData,
  getCookieAsync,
  getMasqueradeModeFromCookie,
  removeCookieAsync,
  removeMasqueradeModeFromCookie,
  removeQrFromCookie,
  removeUrlFromCookie,
} from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import { AccountCircle, Logout } from "@mui/icons-material";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import SupportIcon from "@mui/icons-material/Support";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ChatIcon from "@mui/icons-material/Chat";
import QuizIcon from "@mui/icons-material/Quiz";
import CommitIcon from "@mui/icons-material/Commit";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import ShareIcon from "@mui/icons-material/Share";
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import { setAdminToken, setIsPartner, setMerchantId, setPartnerToken } from "../../utils/reduxSlice/tokenSlice";
import axios from "axios";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  "&::-webkit-scrollbar": {
    width: 0,
  },
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
  "&::-webkit-scrollbar": {
    width: 0,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function PartnerContainer({ children }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // drawer open state logic start
  const [open, setOpen] = useState(false);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };
  // drawer open state logic end

  const [drawerNestedRow, setDrawerNestedRow] = useState({
    report: false,
  });

  // drawer list logic start
  const handleNavigation = (obj) => {
    if (!obj.isChildren) {
      const keys = {};
      for (const key in drawerNestedRow) {
        keys[key] = false;
      }
      setDrawerNestedRow(keys);
    }
    navigate(obj.navLink);
  };

  const handleLogout = async () => {
    await clearPartnerCookieData();
    window.location = "/partner/login";
  };

  const handleDrawerNestedRow = (key) => {
    setOpen(true);
    setDrawerNestedRow({
      ...drawerNestedRow,
      [key]: !drawerNestedRow[key],
    });
  };

  const isLinkSelected = (obj, location) => {
    if (obj.isChildren) {
      setOpen(true);
      setDrawerNestedRow({
        ...drawerNestedRow,
        [obj.parentKey]: true,
      });
    }

    if (obj.navLink === "/partner") {
      return location.pathname === obj.navLink;
    }

    return location.pathname.startsWith(obj.navLink);
  };

  const drawerList = [
    {
      label: t("Dashboard"),
      navLink: "/partner",
      icon: <DashboardIcon />,
      divider: false,
      action: handleNavigation,
    },
    {
      label: t("Product Offering"),
      navLink: "/partner/product-offering",
      icon: <LocalOfferIcon />,
      divider: false,
      action: handleNavigation,
    },
    {
      label: t("Marketing Support"),
      navLink: "/partner/marketing-support",
      icon: <SupportIcon />,
      divider: false,
      action: handleNavigation,
    },
    {
      label: t("Reports"),
      navLink: "/partner/reports",
      icon: <AssessmentIcon />,
      divider: false,
      action: handleNavigation,
    },
    {
      label: t("Communication"),
      navLink: "/partner/communication",
      icon: <ChatIcon />,
      divider: false,
      action: handleNavigation,
    },
    {
      label: t("FAQ"),
      navLink: "/partner/faqs",
      icon: <QuizIcon />,
      divider: false,
      action: handleNavigation,
    },
    {
      label: t("Referral"),
      navLink: "/partner/referral",
      icon: <ShareIcon />,
      divider: false,
      action: handleNavigation,
    },
    {
      label: t("Training Material"),
      navLink: "/partner/training-material",
      icon: <ModelTrainingIcon />,
      divider: false,
      action: handleNavigation,
    },
    {
      label: t("Custom Funnel"),
      navLink: "/partner/customFunnel",
      icon: <BarChartIcon />,
      divider: false,
      action: handleNavigation,
    },
    {
      label: t("Digital Integration"),
      navLink: "/partner/digital-integration",
      icon: <IntegrationInstructionsIcon />,
      divider: false,
      action: handleNavigation,
    },
  ];

  const logoURL = useSelector((state) => state.appInfo.logo);

  const renderListItemButton = (obj, open, location, isChildren) => {
    return (
      <ListItemButton
        key={obj.label}
        sx={{
          minHeight: 48,
          justifyContent: open ? "initial" : "center",
          px: 2.5,
          borderRadius: 1,
          whiteSpace: open ? "normal" : "inherit",
        }}
        selected={isLinkSelected(obj, location)}
        onClick={() =>
          obj.children?.length > 0
            ? handleDrawerNestedRow(obj.key)
            : obj.action(obj)
        }
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: open ? 3 : "auto",
            justifyContent: "center",
            pl: open && isChildren ? 2 : 0,
          }}
        >
          {obj.icon}
        </ListItemIcon>
        <ListItemText primary={obj.label} sx={{ opacity: open ? 1 : 0 }} />
        {open &&
          obj.children &&
          obj.children.length > 0 &&
          (drawerNestedRow[obj.key] ? <ExpandLess /> : <ExpandMore />)}
      </ListItemButton>
    );
  };

  const drawerListItemButton = (obj, open, location, isChildren) => {
    return !open ? (
      <Tooltip key={obj.label} title={obj.label} placement="right">
        {renderListItemButton(obj, open, location, isChildren)}
      </Tooltip>
    ) : (
      renderListItemButton(obj, open, location, isChildren)
    );
  };

  const handleMenuClick = () => {
    navigate("/partner/settings");
    setAnchorEl(null);
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const [isMarqueradeMode, setIsMarqueradeMode] = useState(false);

  useEffect(() => {
    const marqueeMode = getMasqueradeModeFromCookie();
    if (marqueeMode === "true") {
      setIsMarqueradeMode(true);
    } else {
      setIsMarqueradeMode(false);
    }
  }, []);

  const handleExitMarquerade = async () => {
    await removeCookieAsync("p_atkn")
    dispatch(setPartnerToken(null))

    const adminToken = await getCookieAsync("a_atkn")
    dispatch(setAdminToken(adminToken))
    axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;

    await removeCookieAsync("merchantId")
    dispatch(setMerchantId(null))

    await removeCookieAsync("isPartner")
    dispatch(setIsPartner(null))

    removeMasqueradeModeFromCookie()
    removeQrFromCookie()
    removeUrlFromCookie()
    navigate("/admin/merchant-management")
  };

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        open={open}
        color="inherit"
        sx={{
          borderBottom: "1px solid",
          borderColor: "border.main",
          boxShadow: "none",
        }}
      >
        <Toolbar sx={{ marginLeft: { xs: "5px", sm: "0px", md: "0px" } }}>
          <IconButton
            color="inherit"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ marginRight: 5, ...(open && { display: "none" }) }}
          >
            <MenuIcon />
          </IconButton>
          <img
            src={logoURL}
            alt={app_title}
            style={{ maxHeight: 75, maxWidth: 202, padding: 5 }}
          />

          {/* Add MUI dropdown when click on the ICON Buton */}
          <Box
            sx={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {isMarqueradeMode && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #e30b0b",
                  borderRadius: 0.5,
                  px: 2,
                  py: 0.5,
                  ml: 2,
                  backgroundColor: "white",
                  height: "40px",
                }}
              >
                <TheaterComedyIcon sx={{ color: "#e30b0b", fontSize: 20, mr: 1 }} />

                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#e30b0b",
                    mr: 1,
                    whiteSpace: "nowrap",
                  }}

                >
                  {t("Masquerade Mode")}
                </Typography>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: "#e30b0b", mx: 1 }}
                />
                <Button
                  onClick={handleExitMarquerade}
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#e30b0b",
                    textTransform: "none",
                    minWidth: "unset",
                    px: 1,
                  }}
                >
                  Exit
                </Button>
              </Box>
            )}

            <IconButton
              color="inherit"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              aria-controls={open ? "account-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl) ? "true" : undefined}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="account-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              <MenuItem onClick={handleMenuClick}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                {t("Settings")}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                {t("Logout")}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {drawerList.map((obj, idx) => (
            <ListItem
              key={idx}
              dense
              sx={{ display: "block", px: 1 }}
              divider={obj.divider}
            >
              <>
                {drawerListItemButton(obj, open, location, false)}
                {obj.children?.length > 0 && (
                  <Collapse
                    in={drawerNestedRow[obj.key]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <List>
                      {obj.children.map((child) =>
                        drawerListItemButton(child, open, location, true)
                      )}
                    </List>
                  </Collapse>
                )}
              </>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3, overflow: "auto" }}>
        <DrawerHeader />
        <Box>{children}</Box>
      </Box>
    </Box>
  );
}
